import { useState, useEffect } from "react";
import { useStudioEditor } from "../../../store/studioEditorStore";

import classes from "../../StudioErstellen/StudioSize.module.css";
import ErrorMessage from "../../StudioErstellen/ErrorMessage";

export const StudioSizeEditor = ({ studio, validate }) => {
    const { updateField, updatedFields } = useStudioEditor();

    // Mirror the structure from `StudioSize.jsx`
    const [formData, setFormData] = useState({
        studioSize: "",
        productionAreaSize: "",
        capacity: ""
    });

    // Sync initial values from studio / updatedFields, similar to `studioData` in create step
    useEffect(() => {
        if (!studio) return;

        const sizeValue = String(
            updatedFields.studioSize ??
            studio.studioSize ??
            ""
        );

        const productionAreaValue = String(
            updatedFields.productionAreaSize ??
            studio.productionAreaSize ??
            ""
        );

        const capacityValue = String(
            updatedFields.numberOfPeople ??
            studio.numberOfPeople ??
            ""
        );

        setFormData({
            studioSize: sizeValue,
            productionAreaSize: productionAreaValue,
            capacity: capacityValue
        });
        // Only hydrate local state on studio changes. User inputs update the editor store.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studio]);

    const [errors, setErrors] = useState({
        studioSize: "",
        productionAreaSize: "",
        capacity: "",
    });

    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth <= 742 : false
    );

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 742);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Only allow positive integers (remove any non-digit characters)
        const sanitizedValue = value.replace(/[^0-9]/g, "");

        // Local state (like in `StudioSize.jsx`)
        setFormData(prevData => ({
            ...prevData,
            [name]: sanitizedValue
        }));

        // Map to editor store fields
        if (name === "capacity") {
            updateField("numberOfPeople", sanitizedValue);
        } else {
            updateField(name, sanitizedValue);
        }
    };

    const handleKeyDown = (e) => {
        // Prevent: decimal point, comma, minus, plus, 'e', 'E'
        if ([".", ",", "-", "+", "e", "E"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleValidation = () => {
        let isValid = true;
        const newErrors = {
            studioSize: "",
            capacity: "",
            productionAreaSize: "",
        };

        const capacityStr = String(formData.capacity ?? "").trim();
        const studioSizeStr = String(formData.studioSize ?? "").trim();
        const productionAreaStr = String(formData.productionAreaSize ?? "").trim();

        // Capacity (matches `StudioSize.jsx` behaviour)
        if (capacityStr === "") {
            newErrors.capacity = "Personenanzahl muss ausgefüllt werden";
            isValid = false;
        } else {
            const capacityValue = Number(capacityStr);
            if (capacityValue < 1) {
                newErrors.capacity = "Mindestens 1 Person.";
                isValid = false;
            } else if (capacityValue > 1000) {
                newErrors.capacity = "Maximal 1000 Personen.";
                isValid = false;
            }
        }

        // Studio size
        if (studioSizeStr === "") {
            newErrors.studioSize = "Größe muss ausgefüllt werden.";
            isValid = false;
        } else {
            const studioSizeValue = Number(studioSizeStr);
            if (studioSizeValue < 1) {
                newErrors.studioSize = "Größe muss mindestens 1m² betragen.";
                isValid = false;
            } else if (studioSizeValue > 5000) {
                newErrors.studioSize = "Größe darf maximal 5000m² betragen.";
                isValid = false;
            }
        }

        // Production area size
        if (productionAreaStr.trim() === "") {
            newErrors.productionAreaSize = "Größe der Produktionsfläche muss ausgefüllt werden.";
            isValid = false;
        } else {
            const value = Number(productionAreaStr);
            if (isNaN(value) || value < 1) {
                newErrors.productionAreaSize = "Größe der Produktionsfläche muss mindestens 1m² betragen.";
                isValid = false;
            } else if (value > 5000) {
                newErrors.productionAreaSize = "Größe der Produktionsfläche darf maximal 5000m² betragen.";
                isValid = false;
            }
        }

        // Production area must not be larger than total studio size
        const studioSizeNum = Number(studioSizeStr);
        const productionAreaNum = Number(productionAreaStr);
        if (
            !isNaN(studioSizeNum) &&
            !isNaN(productionAreaNum) &&
            productionAreaNum > studioSizeNum &&
            !newErrors.productionAreaSize
        ) {
            newErrors.productionAreaSize = "Größe der Produktionsfläche darf nicht größer als die Gesamtfläche des Studios sein.";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    useEffect(() => {
        if (validate && validate.current) {
            validate.current = handleValidation;
        }
    }, [validate, formData]);

    return (
        <div className={classes.mainContainer}>
            <div className={classes.textContainer}>
                <h2 className={`${classes.title} titleSemibold`}>Studio Größe & Kapazität</h2>
            </div>
            <div className={classes.inputContainer}>
                <div className={classes.inputWrapper} style={{ width: isMobile ? "100%" : "" }}>
                    <h3 className={`${classes.inputTitle} semiboldMiddle `}>Wie groß ist dein Studio?</h3>
                    <div className={classes.sizeContainer} style={{ width: isMobile ? "100%" : "366px" }}>
                        <input
                            name="studioSize"
                            className="slimRegularLargeText"
                            type="number"
                            id="studioSize"
                            placeholder="Größe des Studios"
                            value={formData.studioSize}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            min="1"
                            step="1"
                            pattern="[0-9]*"
                        />
                        <div className={classes.inputDescriptionWrapper}>
                            <span className={`${classes.inputDescription} slimRegularLargeText`}>m²</span>
                        </div>
                    </div>
                    {errors.studioSize && <ErrorMessage>{errors.studioSize}</ErrorMessage>}
                </div>

                <div className={classes.inputWrapper} style={{ width: window.innerWidth > 742 ? "" : "100%" }}>
                    <h3 className={`${classes.inputTitle} semiboldMiddle `}>Wie groß ist die Produktionsfläche?</h3>
                    <div className={classes.sizeContainer} style={{ width: window.innerWidth > 742 ? "366px" : "100%" }}>
                        <input
                            name="productionAreaSize"
                            className="slimRegularLargeText"
                            type="number"
                            id="productionAreaSize"
                            placeholder="Größe der Produktionsfläche"
                            value={formData.productionAreaSize}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            min="1"
                            step="1"
                            pattern="[0-9]*"
                        />
                        <div className={classes.inputDescriptionWrapper}>
                            <span className={`${classes.inputDescription} slimRegularLargeText`}>m²</span>
                        </div>
                    </div>
                    {errors.productionAreaSize && <ErrorMessage>{errors.productionAreaSize}</ErrorMessage>}
                </div>

                <div className={classes.peopleContainer} style={{ width: isMobile ? "100%" : "" }}>
                    <h3 className={`${classes.inputTitle} semiboldMiddle`}>Wie viele Personen passen in dein Studio?</h3>
                    <input
                        style={{ width: isMobile ? "100%" : "366px" }}
                        className="slimRegularLargeText"
                        name="capacity"
                        type="number"
                        id="studioCapacity"
                        placeholder="Personenanzahl"
                        value={formData.capacity}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        min="1"
                        step="1"
                        pattern="[0-9]*"
                    />
                    {errors.capacity && <ErrorMessage>{errors.capacity}</ErrorMessage>}
                </div>
            </div>
        </div>
    );
};

export default StudioSizeEditor;
